import type { ReactNode } from 'react';
import { useSelector } from 'react-redux';

import {
  Box,
  Card,
  FieldSet,
  FormField,
  Icon,
  Label,
  LabeledControl,
  Paragraph,
  Radio,
  Select,
  Tag,
  Text,
} from '@zillow/constellation-10';
import { IconChevronRightOutline, IconWarningFilled } from '@zillow/constellation-icons';

import type { RootState } from '../../../store/types';
import { getFormattedPaymentMethod } from '@common/constants/PaymentMethod';
import type { RenterPaymentMethodInterface } from '@renter-hub/models/Types';
import { getVerifiedPaymentMethods } from '@common/paymentsUtils/multiplePaymentMethodsUtil';
import { VERIFICATION_STATUS } from '@common/constants/PlaidRequirement';

interface Props {
  isLoading?: boolean;
  onOptionChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onPaymentMethodClick?: (paymentMethod: RenterPaymentMethodInterface) => void;
  selectedPaymentMethodId?: string;
  renderSelect?: boolean;
  renderRadioView?: boolean;
  filterStatuses?: Array<string>;
  legend?: ReactNode;
  description?: ReactNode;
}

const AvailablePaymentMethods = ({
  isLoading,
  onOptionChange,
  onPaymentMethodClick,
  selectedPaymentMethodId,
  renderSelect,
  renderRadioView,
  filterStatuses,
  legend,
  description,
}: Props) => {
  const paymentMethods = useSelector((state: RootState) => state.payments.renter.dashboard.paymentMethods);

  const getDisplayPaymentMethods = () => {
    let methods = paymentMethods;

    if (renderRadioView || renderSelect) {
      methods = getVerifiedPaymentMethods(methods);
    }

    if (filterStatuses?.length) {
      methods = methods.filter((pm) => !pm.paymentMethodStatus || !filterStatuses.includes(pm.paymentMethodStatus));
    }

    return methods;
  };

  const displayPaymentMethods = getDisplayPaymentMethods();

  if (renderSelect) {
    return (
      <FormField
        disabled={isLoading}
        label={<Label>Payment method</Label>}
        control={
          <Select value={selectedPaymentMethodId} onChange={onOptionChange}>
            {displayPaymentMethods?.map((paymentMethod) => (
              <option key={paymentMethod.id} value={paymentMethod.id}>
                {`${paymentMethod.nickname || paymentMethod.displayName} ....${paymentMethod.accountLast4}`}
              </option>
            ))}
          </Select>
        }
        description={description}
      />
    );
  }

  if (renderRadioView) {
    return (
      <FieldSet aria-label={legend ? undefined : 'Available payment methods'} legend={legend} description={description}>
        {displayPaymentMethods?.map((paymentMethod) => (
          <LabeledControl
            key={paymentMethod.id}
            control={
              <Radio
                name="payment-method"
                value={paymentMethod.id}
                checked={selectedPaymentMethodId === paymentMethod.id}
                onChange={() => onPaymentMethodClick?.(paymentMethod)}
              />
            }
            label={
              <Label>
                {paymentMethod.nickname || paymentMethod.displayName}
                <br />
                <Text css={{ color: 'text.subtle' }}>
                  {`${getFormattedPaymentMethod(paymentMethod.accountType)} ....${paymentMethod.accountLast4}`}
                </Text>
              </Label>
            }
          />
        ))}
      </FieldSet>
    );
  }

  const getSubText = (paymentMethod: RenterPaymentMethodInterface) => (
    <Box css={{ display: 'flex', flexDirection: 'column' }}>
      <Paragraph textStyle="body-lg-bold">{paymentMethod.nickname || paymentMethod.displayName}</Paragraph>
      <Paragraph css={{ color: 'text.subtle' }} textStyle="body-sm">
        {`${getFormattedPaymentMethod(paymentMethod.accountType)} ....${paymentMethod.accountLast4}`}
      </Paragraph>
    </Box>
  );

  const getTagText = (paymentMethod: RenterPaymentMethodInterface) => {
    if (paymentMethod.paymentMethodStatus === 'pendingAutomaticVerification') {
      return 'Verification in progress';
    } else if (paymentMethod.paymentMethodStatus === 'pendingManualVerification') {
      return 'Verification needed';
    } else if (paymentMethod.paymentMethodStatus === VERIFICATION_STATUS.PLAID_REVOKED) {
      return 'Reconnection needed';
    }
    return null;
  };

  return (
    <Box
      asChild
      css={{ display: 'flex', flexDirection: 'column', gap: 'default', listStyle: 'none', margin: 0, padding: 0 }}
    >
      <ul aria-label="Payment methods">
        {displayPaymentMethods?.map((paymentMethod) => (
          <li key={paymentMethod.id}>
            <Card
              interactive={true}
              elevated={false}
              css={{ padding: 'default' }}
              onClick={() => onPaymentMethodClick?.(paymentMethod)}
            >
              <Box
                css={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 'tighter',
                  flexWrap: 'wrap',
                  rowGap: 'tighter',
                }}
              >
                <Box css={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                  {getSubText(paymentMethod)}
                </Box>
                <Box css={{ display: 'flex', alignItems: 'center', gap: 'default', flexShrink: 0 }}>
                  {(paymentMethod.paymentMethodStatus === 'pendingAutomaticVerification' ||
                    paymentMethod.paymentMethodStatus === 'pendingManualVerification' ||
                    paymentMethod.paymentMethodStatus === VERIFICATION_STATUS.PLAID_REVOKED) && (
                    <Tag icon={<IconWarningFilled />} css={{ whiteSpace: 'nowrap' }} size="md" tone="warning">
                      {getTagText(paymentMethod)}
                    </Tag>
                  )}
                  <Icon size="md" css={{ color: 'brand' }} render={<IconChevronRightOutline />} />
                </Box>
              </Box>
            </Card>
          </li>
        ))}
      </ul>
    </Box>
  );
};

export default AvailablePaymentMethods;
